/**
 * 랭킹 관련 유틸리티 함수들
 */

export interface Badge {
  id: string;
  name: string;
  icon: string;
  color: string;
}

/**
 * 랭킹별 배지 정의
 */
export const RANKING_BADGES: Record<number, Badge> = {
  1: {
    id: 'medal_1',
    name: '1등 메달',
    icon: '1',
    color: '#FFD700',
  },
  2: {
    id: 'medal_2',
    name: '2등 메달',
    icon: '2',
    color: '#C0C0C0',
  },
  3: {
    id: 'medal_3',
    name: '3등 메달',
    icon: '3',
    color: '#CD7F32',
  },
  4: {
    id: 'medal_4',
    name: '4등 메달',
    icon: '4',
    color: '#87CEEB',
  },
  5: {
    id: 'medal_5',
    name: '5등 메달',
    icon: '5',
    color: '#DDA0DD',
  },
  6: {
    id: 'medal_6',
    name: '6등 메달',
    icon: '6',
    color: '#98FB98',
  },
  7: {
    id: 'medal_7',
    name: '7등 메달',
    icon: '7',
    color: '#F0E68C',
  },
  8: {
    id: 'medal_8',
    name: '8등 메달',
    icon: '8',
    color: '#FFA07A',
  },
  9: {
    id: 'medal_9',
    name: '9등 메달',
    icon: '9',
    color: '#20B2AA',
  },
  10: {
    id: 'medal_10',
    name: '10등 메달',
    icon: '10',
    color: '#778899',
  },
};

/**
 * 랭킹에 따른 배지 반환
 */
export function getBadgeByRank(rank: number): Badge | null {
  return RANKING_BADGES[rank] || null;
}

/**
 * 수익률 계산 (기본금액 1000만원 고정)
 */
export function calculateProfitRate(currentBalance: number): number {
  const initialBalance = 10000000; // 1000만원 고정
  // 잔고가 0원이면 -100% 반환
  if (currentBalance <= 0) {
    return -100;
  }
  return ((currentBalance - initialBalance) / initialBalance) * 100;
}

/**
 * 수익률 포맷팅
 */
export function formatProfitRate(profitRate: number): string {
  const sign = profitRate >= 0 ? '+' : '';
  return `${sign}${profitRate.toFixed(2)}%`;
}

/**
 * 수익률에 따른 색상 반환
 */
export function getProfitRateColor(profitRate: number): string {
  if (profitRate > 0) return '#22c55e'; // 초록색 (수익)
  if (profitRate < 0) return '#ef4444'; // 빨간색 (손실)
  return '#6b7280'; // 회색 (무변화)
}

/**
 * 서버 데이터를 클라이언트 랭킹 데이터로 변환
 */
export interface ServerRankingData {
  rank: number;
  userKey: number;
  nickname: string;
  balance: number;
  gameCount: number;
  updatedAt: string;
  isPublicProfile?: boolean;
  selectedTitle?: {
    titleId: number;
    name: string;
    icon: string;
    rarity: 'common' | 'rare' | 'epic' | 'legendary';
  } | null;
}

export interface ClientRankingData extends ServerRankingData {
  profitRate: number;
  profitRateFormatted: string;
  badge: Badge | null;
}

/**
 * SVG 트로피 생성
 */
export function createTrophySVG(
  rank: number,
  size: 'small' | 'large' = 'small'
): string {
  const badge = getBadgeByRank(rank);
  if (!badge) return '';

  const width = size === 'large' ? 100 : 80;
  const height = size === 'large' ? 120 : 96;
  const scale = size === 'large' ? 1 : 0.8;

  // 등급별 색상 설정
  const colors = {
    1: { primary: '#FFD700', secondary: '#FFA500', accent: '#FFFF99' }, // 골드
    2: { primary: '#C0C0C0', secondary: '#A8A8A8', accent: '#E6E6FA' }, // 실버
    3: { primary: '#CD7F32', secondary: '#B8860B', accent: '#DEB887' }, // 브론즈
    4: { primary: '#87CEEB', secondary: '#4682B4', accent: '#B0E0E6' }, // 스카이블루
    5: { primary: '#DDA0DD', secondary: '#BA55D3', accent: '#E6E6FA' }, // 플럼
    6: { primary: '#98FB98', secondary: '#32CD32', accent: '#F0FFF0' }, // 라이트그린
    7: { primary: '#F0E68C', secondary: '#DAA520', accent: '#FFFACD' }, // 카키
    8: { primary: '#FFA07A', secondary: '#FF6347', accent: '#FFE4E1' }, // 라이트살몬
    9: { primary: '#20B2AA', secondary: '#008B8B', accent: '#AFEEEE' }, // 라이트시그린
    10: { primary: '#778899', secondary: '#696969', accent: '#D3D3D3' }, // 라이트슬레이트그레이
  };

  const color = colors[rank as keyof typeof colors] || colors[10];

  return `
    <svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="trophyGrad${rank}" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stop-color="${color.accent}"/>
          <stop offset="1" stop-color="${color.primary}"/>
        </linearGradient>
        <filter id="glow${rank}" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="3" stdDeviation="4" flood-opacity="0.3"/>
        </filter>
      </defs>
      
      <!-- 손잡이 -->
      <path d="M${15 * scale} ${35 * scale} C${5 * scale} ${30 * scale}, ${
    5 * scale
  } ${60 * scale}, ${15 * scale} ${60 * scale}" 
            fill="none" stroke="${color.secondary}" stroke-width="${
    4 * scale
  }" stroke-linecap="round" />
      <path d="M${85 * scale} ${35 * scale} C${95 * scale} ${30 * scale}, ${
    95 * scale
  } ${60 * scale}, ${85 * scale} ${60 * scale}" 
            fill="none" stroke="${color.secondary}" stroke-width="${
    4 * scale
  }" stroke-linecap="round" />
      
      <!-- 트로피 컵 -->
      <g filter="url(#glow${rank})" class="trophy-cup">
        <path d="M${20 * scale} ${30 * scale} Q${30 * scale} ${10 * scale},${
    50 * scale
  } ${10 * scale} Q${70 * scale} ${10 * scale},${80 * scale} ${30 * scale} Q${
    80 * scale
  } ${55 * scale},${50 * scale} ${55 * scale} Q${20 * scale} ${55 * scale},${
    20 * scale
  } ${30 * scale} Z" 
              fill="url(#trophyGrad${rank})" stroke="${
    color.secondary
  }" stroke-width="${1 * scale}"/>
        <rect x="${42.5 * scale}" y="${53 * scale}" width="${
    15 * scale
  }" height="${14 * scale}" rx="${2 * scale}" fill="${color.secondary}" />
        <rect x="${35 * scale}" y="${66 * scale}" width="${
    30 * scale
  }" height="${7 * scale}" rx="${2 * scale}" fill="#333"/>
      </g>
      
      <!-- 숫자 -->
      <text x="${50 * scale}" y="${38 * scale}" text-anchor="middle" 
            font-family="Arial, sans-serif" font-size="${
              14 * scale
            }" font-weight="bold" 
            fill="white" stroke="#000" stroke-width="${
              0.5 * scale
            }">${rank}</text>
      
      <!-- 별 (1-3등만) -->
      ${
        rank <= 3
          ? `
        <polygon class="trophy-star" 
                 points="${50 * scale},${21 * scale} ${53 * scale},${
              29 * scale
            } ${61.5 * scale},${29 * scale} ${55 * scale},${34 * scale} ${
              58 * scale
            },${42 * scale} ${50 * scale},${37 * scale} ${42 * scale},${
              42 * scale
            } ${45 * scale},${34 * scale} ${38.5 * scale},${29 * scale} ${
              47 * scale
            },${29 * scale}" 
                 fill="${color.accent}" opacity="0.9" />
      `
          : ''
      }
    </svg>
  `;
}

/**
 * 트로피 배지 스타일 정보 반환
 */
export function getTrophyBadgeStyle(
  rank: number,
  size: 'small' | 'large' = 'small'
) {
  const badge = getBadgeByRank(rank);
  if (!badge) return null;

  // 크기 설정
  const dimensions =
    size === 'large'
      ? { width: 32, height: 32, fontSize: 11 }
      : { width: 24, height: 24, fontSize: 9 };

  // 등급별 스타일 설정
  const styles = {
    1: {
      bg: 'linear-gradient(135deg, #ffd858, #ffb84d)',
      shadow: '0 4px 12px rgba(255, 200, 60, 0.3)',
      emoji: '🏆',
    },
    2: {
      bg: 'linear-gradient(135deg, #e6eef3, #c7d3df)',
      shadow: '0 3px 10px rgba(180, 190, 200, 0.2)',
      emoji: '🥈',
    },
    3: {
      bg: 'linear-gradient(135deg, #d7a77a, #b86f3b)',
      shadow: '0 3px 10px rgba(184, 111, 59, 0.2)',
      emoji: '🥉',
    },
    4: {
      bg: 'linear-gradient(135deg, #90a4b6, #6f8899)',
      shadow: '0 2px 8px rgba(144, 164, 182, 0.15)',
      emoji: '🏅',
    },
    5: {
      bg: 'linear-gradient(135deg, #8fbf9f, #5ea87b)',
      shadow: '0 2px 8px rgba(143, 191, 159, 0.15)',
      emoji: '🎖️',
    },
    6: {
      bg: 'linear-gradient(135deg, #a89bd6, #7e66b8)',
      shadow: '0 2px 8px rgba(168, 155, 214, 0.15)',
      emoji: '⭐',
    },
    7: {
      bg: 'linear-gradient(135deg, #ffdfe0, #ffbfc5)',
      shadow: '0 2px 6px rgba(255, 223, 224, 0.1)',
      emoji: '✨',
    },
    8: {
      bg: 'linear-gradient(135deg, #fff4cf, #ffe09a)',
      shadow: '0 2px 6px rgba(255, 244, 207, 0.1)',
      emoji: '🌟',
    },
    9: {
      bg: 'linear-gradient(135deg, #e8f6ff, #cbe8ff)',
      shadow: '0 2px 6px rgba(232, 246, 255, 0.1)',
      emoji: '💫',
    },
    10: {
      bg: 'linear-gradient(135deg, #f3f3f3, #e0e0e0)',
      shadow: '0 2px 6px rgba(243, 243, 243, 0.1)',
      emoji: '🔸',
    },
  };

  const style = styles[rank as keyof typeof styles] || styles[10];

  return {
    dimensions,
    style,
    rank,
  };
}

export function transformRankingData(
  serverData: ServerRankingData[]
): ClientRankingData[] {
  return serverData.map(item => {
    const profitRate = calculateProfitRate(item.balance);
    const badge = getBadgeByRank(item.rank);

    return {
      ...item,
      profitRate,
      profitRateFormatted: formatProfitRate(profitRate),
      badge: badge
        ? {
            id: badge.id,
            name: badge.name,
            icon: item.rank.toString(), // 단순히 숫자만 전달
            color: badge.color,
          }
        : null,
    };
  });
}
